import { useSyncExternalStore } from 'react'

/**
 * **Ein Konto wird im Bericht ausgeschnitten und unter einer Position
 * eingefügt** (7.3 Abschnitt 31, R1642).
 *
 * Der Auftrag vom 2026-10-12: *„Rechtsklick auf das Konto => ‚Ausschneiden'
 * sowie der zweite Punkt ‚Einfügen'. Damit kann ich das Konto innerhalb meiner
 * Berichte umhängen, um Fehler beim Mapping zu korrigieren."*
 *
 * **Eine Ablage für alle Berichtsbilder:** Bilanz, GuV, die Spaltenbilder und
 * die Spiegel bauen ihr Menü über `berichtsmenue`, und das fragt hier, was
 * ausgeschnitten ist. Eingefügt wird im Einfügefenster, das einmal in der
 * Anwendung steht — über dieselbe Stelle wie im Positionsfenster und im
 * Gliederungsbaum.
 *
 * **Seit dem 2026-10-14 auch eine Position** (7.3 Abschnitt 32, R1697): Der
 * Auftrag — *„Diese soll denselben Regeln unterliegen wie die Funktion bei
 * den Konten."* Es bleibt eine Ablage: Eingefügt wird, was zuletzt
 * ausgeschnitten wurde, Konto oder Position.
 */

export type Ausgeschnitten = { art: 'konto' | 'position'; schluessel: string; name: string }

/** Was das Einfügen braucht — die Position, unter die das Ausgeschnittene soll, und der Monat des Berichts. */
export type Einfuegeauftrag = Ausgeschnitten & { position: string; positionsname: string; periode: number | null }

/** Das Ereignis, mit dem ein Menü das Einfügefenster ruft. */
export const EINFUEGEN = 'coko:einfuegen'

const AENDERUNG = 'coko:zwischenablage'

let inhalt: Ausgeschnitten | null = null
let erlaubt = false

function melde(): void {
  window.dispatchEvent(new Event(AENDERUNG))
}

/** Was ausgeschnitten ist — `null`, wenn nichts. */
export function ausgeschnitten(): Ausgeschnitten | null {
  return inhalt
}

/** Rechtsklick → *Ausschneiden* an einer Konto- oder Positionszeile. */
export function schneideAus(art: Ausgeschnitten['art'], schluessel: string, name: string): void {
  inhalt = { art, schluessel, name }
  melde()
}

/** Eingefügt oder mit Esc verworfen: Die Ablage ist wieder leer. */
export function verwirf(): void {
  if (inhalt !== null) {
    inhalt = null
    melde()
  }
}

/**
 * Ob der Anwender die Gliederung umbauen darf (R1180: Was er nicht darf, ist auf
 * keiner Seite da). Das Einfügefenster setzt es aus den Rechten — das Menü ist
 * keine Komponente und kann sie selbst nicht fragen.
 */
export function umhaengenErlaubt(): boolean {
  return erlaubt
}

export function setzeErlaubnis(darf: boolean): void {
  erlaubt = darf
  if (!darf) {
    verwirf()
  }
}

/** Ruft das Einfügefenster für diese Position. */
export function rufeEinfuegen(auftrag: Einfuegeauftrag): void {
  window.dispatchEvent(new CustomEvent<Einfuegeauftrag>(EINFUEGEN, { detail: auftrag }))
}

function abonniere(rueckruf: () => void): () => void {
  window.addEventListener(AENDERUNG, rueckruf)
  return () => window.removeEventListener(AENDERUNG, rueckruf)
}

/** Was ausgeschnitten ist — als Zustand für eine Komponente. */
export function useAusgeschnitten(): Ausgeschnitten | null {
  return useSyncExternalStore(abonniere, ausgeschnitten, ausgeschnitten)
}
